import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

/**
 * MyQR — Shannon's flash-from-phone recruitment card.
 *
 * When she meets a contractor at Home Depot, gas station, or a job site,
 * she pulls this URL up on her phone. Big QR code, big font, tap-to-share.
 * They scan → land at /apply → fill out the vendor wizard → get texted
 * when jobs match their trade + area.
 *
 * URL: /my-qr (or /qr — both routes point here)
 */

const BG = "#020A1F";
const CYAN = "#00FFFF";
const WHITE = "#FFFFFF";
const SILVER = "#A0A0A0";

const MONO = "'JetBrains Mono', 'SF Mono', Menlo, monospace";
const SANS = "'Inter', -apple-system, sans-serif";

const injectFonts = () => {
  const ID = "myqr-fonts";
  if (document.getElementById(ID)) return;
  const l = document.createElement("link");
  l.id = ID; l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@600;700&family=Inter:wght@400;600;700;800&display=swap";
  document.head.appendChild(l);
};

const MyQR = () => {
  const [origin, setOrigin] = useState("");

  useEffect(() => {
    injectFonts();
    if (typeof window !== "undefined") setOrigin(window.location.origin);
  }, []);

  // Short URL she says aloud. /apply is aliased in App.js to VendorApplication.
  const applyUrl = useMemo(() => (origin ? `${origin}/apply` : ""), [origin]);
  // Free public QR service — client-only, no API key, no rate limits at this volume.
  const qrSrc = useMemo(
    () => applyUrl ? `https://api.qrserver.com/v1/create-qr-code/?size=520x520&margin=8&color=020A1F&bgcolor=00FFFF&format=svg&data=${encodeURIComponent(applyUrl)}` : "",
    [applyUrl]
  );

  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(applyUrl); } catch (e) { void e; }
  };

  const shareCard = async () => {
    if (!navigator.share) { copyUrl(); return; }
    try {
      await navigator.share({
        title: "Moovin Forward · Contractor Application",
        text: "Moovin Forward LLC is onboarding independent contractors and trades in the DFW metroplex. Apply here — you'll get texted when jobs match your trade + area:",
        url: applyUrl,
      });
    } catch (e) { void e; }
  };

  return (
    <div data-testid="my-qr-root" style={{
      minHeight: "100vh", background: BG, color: WHITE, fontFamily: SANS,
      display: "flex", flexDirection: "column", alignItems: "center",
      padding: "1.5rem 1rem 2rem",
      backgroundImage: `radial-gradient(900px 500px at 50% 0%, ${CYAN}18 0%, transparent 60%), linear-gradient(180deg, ${BG} 0%, #030F2A 100%)`,
    }}>
      {/* Top bar */}
      <div style={{ width: "100%", maxWidth: 600, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.2rem" }}>
        <Link to="/ceo-field" style={{ color: SILVER, textDecoration: "none", fontFamily: MONO, fontSize: "0.68rem", letterSpacing: "0.16em" }} data-testid="myqr-back-field">
          ← FIELD
        </Link>
        <div style={{ color: CYAN, fontFamily: MONO, fontSize: "0.68rem", letterSpacing: "0.22em" }}>
          RECRUITMENT_CARD
        </div>
        <div style={{ width: 40 }} />
      </div>

      {/* Big headline */}
      <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
        <div style={{ color: CYAN, fontFamily: MONO, fontSize: "0.72rem", letterSpacing: "0.28em", marginBottom: 6 }}>
          MOOVIN FORWARD LLC · DFW
        </div>
        <h1 style={{ fontSize: "clamp(1.9rem, 6vw, 2.6rem)", fontWeight: 800, margin: "0 0 0.4rem", lineHeight: 1.1, letterSpacing: "-0.02em" }} data-testid="myqr-headline">
          Scan to apply.
        </h1>
        <div style={{ color: SILVER, fontSize: "0.92rem", lineHeight: 1.5, maxWidth: 420, margin: "0 auto" }}>
          Independent contractor and trades onboarding.<br/>
          You&apos;ll get texted when jobs match your trade and ZIP.
        </div>
      </div>

      {/* QR card */}
      <div data-testid="myqr-card" style={{
        background: WHITE, borderRadius: 16, padding: "1.4rem",
        boxShadow: `0 30px 80px rgba(0,0,0,0.55), 0 0 40px ${CYAN}55`,
        maxWidth: 560, width: "100%",
        display: "flex", flexDirection: "column", alignItems: "center", gap: "0.8rem",
      }}>
        {qrSrc && (
          <img src={qrSrc} alt="Scan to apply — Moovin Forward vendor application"
            data-testid="myqr-image"
            style={{ width: "100%", maxWidth: 460, height: "auto", display: "block" }} />
        )}
        <div style={{ color: BG, fontFamily: MONO, fontSize: "0.82rem", fontWeight: 700, letterSpacing: "0.06em", textAlign: "center", wordBreak: "break-all" }} data-testid="myqr-url">
          {applyUrl.replace(/^https?:\/\//, "")}
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: "flex", gap: 8, marginTop: "1.2rem", width: "100%", maxWidth: 560 }}>
        <button type="button" onClick={shareCard} data-testid="myqr-share"
          style={{
            flex: 1, background: CYAN, color: BG, border: "none", borderRadius: 8,
            padding: "0.95rem 1rem", fontFamily: MONO, fontSize: "0.78rem",
            fontWeight: 800, letterSpacing: "0.08em", cursor: "pointer",
          }}>
          SHARE LINK
        </button>
        <button type="button" onClick={copyUrl} data-testid="myqr-copy"
          style={{
            flex: 1, background: "transparent", color: CYAN, border: `1px solid ${CYAN}`,
            borderRadius: 8, padding: "0.95rem 1rem", fontFamily: MONO, fontSize: "0.78rem",
            fontWeight: 700, letterSpacing: "0.08em", cursor: "pointer",
          }}>
          COPY URL
        </button>
      </div>

      {/* Pocket script */}
      <div style={{ maxWidth: 560, width: "100%", marginTop: "1.6rem",
                    background: "rgba(255,255,255,0.04)", border: `1px solid ${SILVER}33`,
                    borderRadius: 8, padding: "1rem 1.1rem" }} data-testid="myqr-script">
        <div style={{ color: CYAN, fontFamily: MONO, fontSize: "0.62rem", letterSpacing: "0.22em", marginBottom: 6 }}>
          POCKET SCRIPT
        </div>
        <div style={{ color: WHITE, fontSize: "0.9rem", lineHeight: 1.55, fontStyle: "italic" }}>
          &ldquo;My company runs interior turnovers across DFW. If you or somebody you know
          does 1099 contractor work — cleaning, punch-out, cabinet refacing, HVAC, electrical,
          plumbing — scan this. You&apos;ll get texted when we have jobs in your ZIP.&rdquo;
        </div>
      </div>

      {/* Post-application flow disclosure */}
      <div style={{ maxWidth: 560, width: "100%", marginTop: "1rem", padding: "0.9rem 1rem",
                    background: "rgba(255,255,255,0.03)", borderRadius: 6 }} data-testid="myqr-flow">
        <div style={{ color: SILVER, fontFamily: MONO, fontSize: "0.6rem", letterSpacing: "0.18em", marginBottom: 6 }}>
          WHAT HAPPENS AFTER THEY APPLY
        </div>
        <ol style={{ color: WHITE, fontSize: "0.82rem", lineHeight: 1.65, margin: 0, paddingLeft: "1.2rem" }}>
          <li>Application lands in your vendor database instantly.</li>
          <li>Auto-scored on trade, tenure, license, ownership tags.</li>
          <li>Routed as <strong style={{ color: CYAN }}>Tier A</strong> (direct crew paid by you) or <strong style={{ color: CYAN }}>Tier B</strong> (referral to LP direct).</li>
          <li>When a job matches their trade + ZIP, they get an SMS via Twilio.</li>
        </ol>
      </div>

      <div style={{ marginTop: "1.5rem", color: SILVER, fontFamily: MONO, fontSize: "0.62rem", letterSpacing: "0.16em", textAlign: "center" }}>
        MOOVIN FORWARD LLC · INSTITUTIONAL INTERIOR STABILIZATION · DFW METROPLEX
      </div>
    </div>
  );
};

export default MyQR;
